import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import Button from '@/components/Button';
import Modal from '@/components/Modals/Modal';
import { LABELS_BATCH_SIZE } from '@/constants/env';
import { LabelPageSizes } from '@/types/parcel/orders/labels.interface';

interface BatchProgress {
  current: number;
  total: number;
}

interface PickupsDownloadModalProps {
  isOpen: boolean;
  isDownloading: boolean;
  batchProgress: BatchProgress | null;
  trackingIds: string[];
  onClose: () => void;
  onDownload: (trackingIds: string[], pageSize: LabelPageSizes) => void;
}

const PickupsDownloadModal = ({
  isOpen,
  isDownloading,
  batchProgress,
  trackingIds,
  onClose,
  onDownload,
}: PickupsDownloadModalProps) => {
  const theme = useTheme();

  const isBatchDownload = trackingIds.length > LABELS_BATCH_SIZE;
  const progressPercent = batchProgress
    ? Math.round((batchProgress.current / batchProgress.total) * 100)
    : 0;
  const spinner = <CircularProgress size={18} sx={{ color: 'white' }} />;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Descargar etiquetas"
      actions={
        <Box
          sx={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'space-evenly',
            width: '100%',
            paddingBottom: '1rem',
          }}
        >
          <Button disabled={isDownloading} onClick={() => onDownload(trackingIds, 'letter')}>
            {isDownloading ? spinner : 'Carta'}
          </Button>
          <Button
            variant="contained"
            disabled={isDownloading}
            onClick={() => onDownload(trackingIds, 'small')}
          >
            {isDownloading ? spinner : 'Small'}
          </Button>
          <Button
            variant="contained"
            disabled={isDownloading}
            onClick={() => onDownload(trackingIds, 'zebra')}
          >
            {isDownloading ? spinner : 'Zebra'}
          </Button>
        </Box>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1rem', alignItems: 'center' }}>
        <CloudDownloadOutlinedIcon sx={{ width: '50px', height: 'auto' }} color="primary" />
        <Typography
          sx={{ color: theme.palette.secondary.main, fontWeight: 'bold', textAlign: 'center' }}
        >
          Selecciona el tamaño que deseas imprimir
        </Typography>

        {isBatchDownload && !isDownloading && (
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, textAlign: 'center' }}
          >
            Se descargarán{' '}
            <strong>{Math.ceil(trackingIds.length / LABELS_BATCH_SIZE)} lotes</strong> de hasta{' '}
            <strong>{LABELS_BATCH_SIZE} etiquetas</strong> cada uno ({trackingIds.length} en total).
          </Typography>
        )}

        {isDownloading && batchProgress && (
          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <Typography
              variant="body2"
              sx={{ textAlign: 'center', color: theme.palette.text.secondary }}
            >
              Descargando lote {batchProgress.current} de {batchProgress.total}...
            </Typography>
            <LinearProgress variant="determinate" value={progressPercent} />
            <Typography
              variant="caption"
              sx={{ textAlign: 'center', color: theme.palette.text.secondary }}
            >
              {progressPercent}%
            </Typography>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default PickupsDownloadModal;
